import type { ReactNode } from 'react';

/**
 * The homepage's two feature sections (4.1.56): the three-step flow
 * under "Yours from the first click." and the six capability cards
 * under "The whole day, in one place." Small reusable pieces — a step,
 * a card, an icon badge, a micro pill — so the sections read as one
 * system, and the copy stays in HomePage.tsx where it always was.
 *
 * Every icon is a line glyph on currentColor, like the rest of the
 * product's icons; the card's own icon is drawn again, large and faint,
 * in its corner as the watermark, so nothing here is a second image to
 * keep in step with the first.
 */

/* ---------------- The three steps ---------------- */

export interface StepContent { title: string; copy: string; art: ReactNode }

export function StepFlow({ steps }: { steps: StepContent[] }) {
  return (
    <ol className="home-steps">
      {steps.map((step, i) => (
        <StepItem key={step.title} number={i + 1} last={i === steps.length - 1} {...step} />
      ))}
    </ol>
  );
}

export function StepItem({ number, title, copy, art, last }: StepContent & { number: number; last: boolean }) {
  return (
    <li className="home-step">
      <div className="home-step__head">
        <span className="home-step__marker" aria-hidden="true">{number}</span>
        <span className="home-step__art" aria-hidden="true">{art}</span>
        {!last && <span className="home-step__line" aria-hidden="true" />}
      </div>
      <h3><span className="sr-only">Step {number}: </span>{title}</h3>
      <p>{copy}</p>
    </li>
  );
}

/** Step 1 — a booking page being made your own: a window, a brush, colour dots. */
export function ArtCustomize() {
  return (
    <svg viewBox="0 0 64 52" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4" y="6" width="56" height="40" rx="8" fill="currentColor" fillOpacity=".12" stroke="none" />
      <rect x="4" y="6" width="56" height="40" rx="8" />
      <path d="M4 17h56" />
      <circle cx="12" cy="11.5" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="17.5" cy="11.5" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="23" cy="11.5" r="1.4" fill="currentColor" stroke="none" />
      <path d="M12 26h18M12 32h12" />
      <path d="M46 22 34 34l-1.5 5 5-1.5L49.5 25.5a2.5 2.5 0 0 0-3.5-3.5Z" fill="currentColor" fillOpacity=".25" />
      <circle cx="16" cy="40" r="2.2" fill="currentColor" stroke="none" />
      <circle cx="23" cy="40" r="2.2" fill="currentColor" stroke="none" fillOpacity=".55" />
      <circle cx="30" cy="40" r="2.2" fill="currentColor" stroke="none" fillOpacity=".3" />
    </svg>
  );
}

/** Step 2 — a link and a QR code. */
export function ArtShare() {
  return (
    <svg viewBox="0 0 64 52" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 30.5 15.5 37a6.5 6.5 0 0 1-9.2-9.2l7-7a6.5 6.5 0 0 1 9.2 0" />
      <path d="M18 21.5 24.5 15a6.5 6.5 0 0 1 9.2 9.2l-7 7a6.5 6.5 0 0 1-9.2 0" />
      <rect x="38" y="14" width="22" height="22" rx="3" fill="currentColor" fillOpacity=".12" />
      <rect x="41" y="17" width="6" height="6" rx="1" fill="currentColor" stroke="none" />
      <rect x="51" y="17" width="6" height="6" rx="1" fill="currentColor" stroke="none" />
      <rect x="41" y="27" width="6" height="6" rx="1" fill="currentColor" stroke="none" />
      <path d="M51 27h2v2h-2zM55 27h2v2h-2zM53 31h2v2h-2zM51 31h-0M55 31h2v2h-2z" fill="currentColor" stroke="none" />
      <path d="M46 40h6M49 37v6" strokeOpacity=".5" />
    </svg>
  );
}

/** Step 3 — a calendar with a booking ticked off. */
export function ArtCalendar() {
  return (
    <svg viewBox="0 0 64 52" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="10" y="9" width="44" height="38" rx="7" fill="currentColor" fillOpacity=".12" stroke="none" />
      <rect x="10" y="9" width="44" height="38" rx="7" />
      <path d="M10 20h44M22 5v8M42 5v8" />
      <rect x="17" y="26" width="6" height="5" rx="1" fill="currentColor" stroke="none" fillOpacity=".55" />
      <rect x="27" y="26" width="6" height="5" rx="1" fill="currentColor" stroke="none" fillOpacity=".35" />
      <rect x="17" y="35" width="6" height="5" rx="1" fill="currentColor" stroke="none" fillOpacity=".35" />
      <rect x="27" y="35" width="6" height="5" rx="1" fill="currentColor" stroke="none" fillOpacity=".2" />
      <circle cx="44" cy="35" r="8" fill="currentColor" stroke="none" />
      <path d="m40.5 35 2.5 2.5 4.5-5" stroke="var(--surface)" strokeWidth="2.2" />
      <path d="M58 20l3-3M60 27h3M58 34l3 3" strokeOpacity=".45" />
    </svg>
  );
}

/* ---------------- The six capability cards ---------------- */

export type Tone = 'lavender' | 'rose' | 'sky' | 'mint' | 'amber' | 'violet';

export interface FeatureContent { title: string; label: string; copy: string; tone: Tone; icon: ReactNode }

export function FeatureCard({ title, label, copy, tone, icon }: FeatureContent) {
  return (
    <article className={`home-capability home-capability--${tone}`}>
      <IconBadge>{icon}</IconBadge>
      <div className="home-capability__body">
        <h3>{title}</h3>
        <MicroPill>{label}</MicroPill>
        <p>{copy}</p>
      </div>
      <span className="home-capability__mark" aria-hidden="true">{icon}</span>
    </article>
  );
}

/** A round tinted badge around a line icon. The tone comes from the card. */
export function IconBadge({ children }: { children: ReactNode }) {
  return <span className="icon-badge" aria-hidden="true">{children}</span>;
}

/** A small tinted label under a title: one short phrase, never a sentence. */
export function MicroPill({ children }: { children: ReactNode }) {
  return <span className="micro-pill">{children}</span>;
}

const line = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.9, strokeLinecap: 'round', strokeLinejoin: 'round' } as const;

export function IconCalendar() {
  return (
    <svg viewBox="0 0 24 24" {...line} aria-hidden="true">
      <rect x="3" y="5" width="18" height="16" rx="3" />
      <path d="M3 10h18M8 3v4M16 3v4" />
      <path d="M8 14h2M12 14h2M16 14h2M8 17.5h2M12 17.5h2" />
    </svg>
  );
}
export function IconPeople() {
  return (
    <svg viewBox="0 0 24 24" {...line} aria-hidden="true">
      <circle cx="9" cy="8.5" r="3.5" />
      <path d="M2.5 20a6.5 6.5 0 0 1 13 0" />
      <circle cx="17" cy="9.5" r="2.6" />
      <path d="M16 15.2a5 5 0 0 1 5.5 4.8" />
    </svg>
  );
}
export function IconPin() {
  return (
    <svg viewBox="0 0 24 24" {...line} aria-hidden="true">
      <path d="M12 21.5s-6.5-6-6.5-11a6.5 6.5 0 0 1 13 0c0 5-6.5 11-6.5 11Z" />
      <circle cx="12" cy="10.5" r="2.4" />
    </svg>
  );
}
export function IconCard() {
  return (
    <svg viewBox="0 0 24 24" {...line} aria-hidden="true">
      <rect x="2.5" y="5.5" width="19" height="13" rx="2.5" />
      <path d="M2.5 10h19M6.5 14.5h4" />
    </svg>
  );
}
export function IconChat() {
  return (
    <svg viewBox="0 0 24 24" {...line} aria-hidden="true">
      <path d="M4 5.5h16a1.5 1.5 0 0 1 1.5 1.5v8a1.5 1.5 0 0 1-1.5 1.5h-8.5L7 20.5V16.5H4A1.5 1.5 0 0 1 2.5 15V7A1.5 1.5 0 0 1 4 5.5Z" />
      <path d="M7.5 10h9M7.5 13h5" />
    </svg>
  );
}
export function IconChart() {
  return (
    <svg viewBox="0 0 24 24" {...line} aria-hidden="true">
      <path d="M3.5 20.5h17" />
      <rect x="5" y="12" width="3.6" height="6" rx="1" />
      <rect x="10.2" y="7.5" width="3.6" height="10.5" rx="1" />
      <rect x="15.4" y="4" width="3.6" height="14" rx="1" />
    </svg>
  );
}
